import { FC, useCallback, useEffect, useState } from 'react';
import SearchBar from 'components/search/SearchBar';
import DropdownWithSearch from 'components/dropdown-with-search/DropdownWithSearch';
import { ReactComponent as EditIconBlue } from 'assets/icons/EditIconBlue.svg';
import { BackIcon } from 'assets/icons';
import { useNavigate } from 'react-router-dom';
import { ButtonType } from 'components/custom-button/CustomButton';
import CustomButton from 'components/custom-button/CustomButton';
import routesPath from 'routes/RoutesPath';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { CustomAvatar, Loader, NoPermissions, Pagination } from 'components';
import { usePaginatedGetServiceAreasQuery } from './hooks/usePaginatedGetServiceAreasQuery';
import { debounce } from 'lodash';
import { useLazyGetAssignableMembersQuery } from 'containers/jobs/api';
import { AssignableMember } from 'containers/jobs/jobs.model';
import AssignableMembersModal from 'containers/jobs/create-edit-job/components/AssignableMembersModal';
import { useGetServiceAreaColorCodesQuery, useUpdateServiceAreaAssigneesMutation } from './api';
import { getInitials } from 'utils/commonUtils';
import { getTextForFilter } from 'containers/dispatch/utils';
import { InfoMesssages } from 'types/infoMessages';

interface FilterState {
  searchValue: string;
  status: string[];
  members: string[];
  colorCode: string[];
}

const ServiceAreasSettings: FC = () => {
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { viewJobSettings, editJobSettings } = useAppSelector(selectCurrentTeamJobPermissions);

  const [statusDraft, setStatusDraft] = useState<string[]>([]);
  const [membersDraft, setMembersDraft] = useState<string[]>([]);
  const [colorDraft, setColorDraft] = useState<string[]>([]);
  const [assignableMembers, setAssignableMembers] = useState<AssignableMember[]>([]);
  const [showAssignMembersModal, setShowAssignMembersModal] = useState(false);
  const [selectedServiceArea, setSelectedServiceArea] = useState<any>(null);

  // Local filter state
  const [filters, setFilters] = useState<FilterState>({
    searchValue: '',
    status: [],
    members: [],
    colorCode: []
  });

  const [getAssignableTeamMemberQuery, { data: assignableMembersData }] = useLazyGetAssignableMembersQuery();
  const [updateServiceAreaAssignees, { isLoading: isUpdatingServiceAreaAssignees }] =
    useUpdateServiceAreaAssigneesMutation();

  const {
    list: serviceAreas,
    isLoading,
    queryParams,
    setQueryParams,
    resetQuery,
    page,
    totalCount,
    fetchData,
    limit,
    isError
  } = usePaginatedGetServiceAreasQuery();

  useEffect(() => {
    resetQuery();
  }, [viewJobSettings]);

  const { data: colorCodesData } = useGetServiceAreaColorCodesQuery(
    { teamId: currentTeam?.id?.toString() || '' },
    { skip: !currentTeam?.id || !serviceAreas?.length }
  );

  const colorCodeOptions =
    colorCodesData?.colorCodes?.map(code => ({
      id: code,
      name: code
    })) || [];

  const renderColorOption = (option: { id: string; name: string }) => (
    <div className="flex items-center gap-2">
      <div className="h-4 w-4 rounded-full" style={{ backgroundColor: option.id }} />
      <span>{option.name}</span>
    </div>
  );

  useEffect(() => {
    if (currentTeam?.id) {
      resetQuery();
      getAssignableTeamMemberQuery(currentTeam.id.toString());
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    if (assignableMembersData?.data?.teamMemberList) {
      setAssignableMembers(assignableMembersData.data.teamMemberList);
    }
  }, [assignableMembersData]);

  // Sync filters with query params
  useEffect(() => {
    setFilters({
      searchValue: queryParams.searchValue || '',
      status: queryParams.status ? queryParams.status.split(',') : [],
      members: queryParams.members ? queryParams.members.split(',') : [],
      colorCode: queryParams.colorCode ? queryParams.colorCode.split(',') : []
    });
  }, [queryParams]);

  const initiateSearch = (searchTerm?: string) => {
    setQueryParams(prevParams => ({ ...prevParams, searchValue: searchTerm }));
  };

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), []);

  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, searchValue: value || '' }));
    debouncedSearchHandler(value);
  };

  const handleClearSearch = () => {
    debouncedSearchHandler('');
  };

  const handleStatusFilter = (values: string[]) => {
    setFilters(prev => ({ ...prev, status: values }));
    setQueryParams(prevParams => ({ ...prevParams, status: values.join(',') }));
  };

  const handleStatusDraft = (values: string[]) => {
    setStatusDraft(values);
  };

  const handleMembersFilter = (values: string[]) => {
    setFilters(prev => ({ ...prev, members: values }));
    setQueryParams(prevParams => ({ ...prevParams, members: values.join(',') }));
  };

  const handleMembersDraft = (values: string[]) => {
    setMembersDraft(values);
  };

  const handleColorFilter = (values: string[]) => {
    setFilters(prev => ({ ...prev, colorCode: values }));
    setQueryParams(prevParams => ({ ...prevParams, colorCode: values.join(',') }));
  };

  const handleColorDraft = (values: string[]) => {
    setColorDraft(values);
  };

  const statusOptions = [
    { id: 'ENABLED', name: 'Enabled' },
    { id: 'DISABLED', name: 'Disabled' }
  ];

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleAddServiceArea = () => {
    navigate(routesPath.ADD_SERVICE_AREA);
  };

  const handleEditServiceArea = (id: string) => {
    navigate(routesPath.EDIT_SERVICE_AREA.replace(':id', id));
  };

  const handleAddMembers = (serviceArea: any) => {
    setSelectedServiceArea(serviceArea);
    setShowAssignMembersModal(true);
  };

  const handleSaveMembers = async (selectedMembers: AssignableMember[]) => {
    if (!currentTeam?.id || !selectedServiceArea) return;

    try {
      await updateServiceAreaAssignees({
        teamId: currentTeam.id.toString(),
        serviceAreaId: selectedServiceArea.id,
        payload: {
          assignees: selectedMembers.map(member => member.merchantId)
        }
      });

      // Refresh the service areas list
      resetQuery();
    } catch (error) {
      // Error handling is already handled by the API mutation
    } finally {
      setShowAssignMembersModal(false);
      setSelectedServiceArea(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    fetchData(newPage - 1);
  };

  const handleResultsPerPageChange = (newLimit: number) => {
    setQueryParams(prevParams => ({ ...prevParams, limit: newLimit }));
  };

  const renderTableContent = () => {
    if (!viewJobSettings) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <NoPermissions />
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex h-[50vh] items-center justify-center p-2">
          <Loader />
        </div>
      );
    }

    if (!serviceAreas || serviceAreas.length === 0) {
      return (
        <div className="flex h-[50vh] items-center justify-center p-2">
          <p className="text-gray-500">No service areas found</p>
        </div>
      );
    }

    return (
      <div className="mx-10 overflow-hidden rounded-lg border border-borderGray">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-[30%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">
                Service area name
              </th>
              <th className="w-[20%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Color code</th>
              <th className="w-[25%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Members</th>
              <th className="w-[15%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Status</th>
              {editJobSettings && (
                <th className="w-[10%] px-6 py-3 text-left text-sbase font-semibold text-primaryText">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {serviceAreas.map(area => (
              <tr key={area.id}>
                <td className="px-6 py-3 text-sbase font-semibold text-primaryText">{area.name}</td>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded-full" style={{ backgroundColor: area.colorCode }} />
                    {area.colorCode}
                  </div>
                </td>
                <td className="px-6 py-3">
                  <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-2">
                        {area.assignees?.slice(0, 3).map(assignee => (
                          <CustomAvatar
                            key={assignee.id}
                            src={assignee.profilePictureURL}
                            label={getInitials(assignee.name)}
                            className="text-sm"
                            width="w-5"
                            height="h-5"
                          />
                        ))}
                      </div>
                      {area.assignees?.length > 3 && <span>+{area.assignees?.length - 3}</span>}
                      {(area.assignees?.length === 0 || !area.assignees) && <span>-</span>}
                    </div>
                    {editJobSettings && (
                      <button className="font-semibold text-secondary" onClick={() => handleAddMembers(area)}>
                        +Add members
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-6 py-3">
                  <span className={area.status === 'ENABLED' ? 'text-successHighlight' : 'text-error'}>
                    {area.status === 'ENABLED' ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                {editJobSettings && (
                  <td className="px-6 py-3">
                    <button
                      className="rounded-md p-1 text-secondary hover:bg-gray-100"
                      onClick={() => handleEditServiceArea(area.id)}>
                      <EditIconBlue className="h-4 w-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="flex h-full w-full flex-col ">
      <div className="mb-4 px-10 pt-7">
        <div className="flex items-center">
          <BackIcon
            className="path-stroke-current mr-5 h-5 w-5 cursor-pointer text-primaryText"
            onClick={handleBackClick}
          />
          <h1 className="text-2xl font-bold text-primaryText">Service areas</h1>
        </div>
        <p className="mt-2 ml-10 text-17px font-normal text-accentText">
          Define your service areas to make team scheduling easy
        </p>
      </div>

      <div className="mb-7 flex items-start justify-between px-10">
        {viewJobSettings && (
          <div className="flex max-w-[calc(100%-180px)] flex-wrap items-center gap-4">
            <div className="w-64">
              <SearchBar
                id="service-area-search"
                value={filters.searchValue}
                handleChange={handleSearchChange}
                handleClear={handleClearSearch}
                placeholder="Search service areas"
                autoFocus={false}
                showSearchIcon={true}
                alignRight={true}
                showCrossIcon={true}
                wrapperStyle="border border-borderGray rounded"
              />
            </div>

            <DropdownWithSearch
              data={statusOptions}
              dataKey="name"
              dataIdKey="id"
              selectedArray={filters.status}
              selectedDraftArray={statusDraft}
              setSelectedArray={handleStatusFilter}
              setSelectedDraftArray={handleStatusDraft}
              buttonPrimaryText="Status"
              buttonSecondaryText={getTextForFilter(statusOptions, filters.status, 'id', 'name')}
              filterPlaceholderText="Select status"
              allowSearch={false}
              filterId="status-filter"
            />

            <DropdownWithSearch
              data={assignableMembers}
              dataKey="name"
              dataIdKey="merchantId"
              selectedArray={filters.members}
              selectedDraftArray={membersDraft}
              setSelectedArray={handleMembersFilter}
              setSelectedDraftArray={handleMembersDraft}
              buttonPrimaryText="Members"
              buttonSecondaryText={getTextForFilter(assignableMembers, filters.members, 'merchantId', 'name')}
              filterPlaceholderText="Select members"
              allowSearch={true}
              filterId="members-filter"
            />

            <DropdownWithSearch
              data={colorCodeOptions}
              dataKey="name"
              dataIdKey="id"
              selectedArray={filters.colorCode}
              selectedDraftArray={colorDraft}
              setSelectedArray={handleColorFilter}
              setSelectedDraftArray={handleColorDraft}
              buttonPrimaryText="Color code"
              buttonSecondaryText={getTextForFilter(colorCodeOptions, filters.colorCode, 'id', 'name')}
              filterPlaceholderText="Select color"
              allowSearch={false}
              filterId="color-filter"
              showCustomOptions={true}
              customOption={renderColorOption}
            />
          </div>
        )}
        <div className="ml-auto">
          <CustomButton
            id="add-service-area"
            type={ButtonType.PRIMARY}
            onClick={handleAddServiceArea}
            showDisabledReason={!editJobSettings}
            disabledReason={InfoMesssages.noPermissionToAddServiceAreas}>
            + Add service area
          </CustomButton>
        </div>
      </div>

      <div className="flex w-full flex-1 flex-col justify-between">
        {isError ? (
          <div className="flex h-[50vh] items-center justify-center p-2">
            <p className="text-error">Error loading service areas. Please try again.</p>
          </div>
        ) : (
          <>
            {renderTableContent()}
            {!isLoading && serviceAreas && serviceAreas.length > 0 && viewJobSettings && (
              <div className="sticky bottom-0 z-10 w-full">
                <Pagination
                  currentPage={page + 1}
                  onPageChange={handlePageChange}
                  totalCount={totalCount}
                  limit={limit}
                  showSelectResultsPerPage={true}
                  wrapperClassName="w-full h-12"
                  onSelectResultsPerPage={handleResultsPerPageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      {showAssignMembersModal && (
        <AssignableMembersModal
          list={assignableMembers}
          selectedMembers={
            (selectedServiceArea?.assignees?.map(assignee => ({
              merchantId: assignee.id,
              name: assignee.name,
              profilePictureUrl: assignee.profilePictureURL,
              email: '',
              phone: ''
            })) as AssignableMember[]) || []
          }
          onClose={() => setShowAssignMembersModal(false)}
          onSave={handleSaveMembers}
          isLoading={isUpdatingServiceAreaAssignees}
        />
      )}
    </div>
  );
};

export default ServiceAreasSettings;
